import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ShieldCheck, TrendingUp, Users } from "lucide-react";
import api from "../lib/api";
import { MaskedLines, Reveal } from "../components/Reveal";
import PropertyCard from "../components/PropertyCard";

const HERO_IMG = "https://images.unsplash.com/photo-1580785693039-7efb961f4b3c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBoaWdoJTIwcmlzZSUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3OTE5NzQyNDR8MA&ixlib=rb-4.1.0&q=85";
const MARBLE = "https://images.unsplash.com/photo-1550053808-52a75a05955d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwxfHxtYXJibGUlMjB0ZXh0dXJlJTIwZGFyayUyMGx1eHVyeXxlbnwwfHx8fDE3OTE5NzQyNTV8MA&ixlib=rb-4.1.0&q=85";
const VILLA = "https://images.unsplash.com/photo-1706808849780-7a04fbac83ef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjB2aWxsYSUyMG1vZGVybiUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3OTE5NzQyNTV8MA&ixlib=rb-4.1.0&q=85";
const INTERIOR = "https://images.unsplash.com/photo-1757924461488-ef9ad0670978?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBwZW50aG91c2UlMjBpbnRlcmlvciUyMG1vZGVybnxlbnwwfHx8fDE3OTE5NzQyNDN8MA&ixlib=rb-4.1.0&q=85";

const MARQUEE_ITEMS = ["Golf Course Road", "DLF Phase 2", "Sector 42", "Sohna Road", "MG Road", "Cyber City", "Golf Course Ext", "Sector 50"];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "45%"]);

  useEffect(() => {
    api.get("/properties", { params: { featured: true, limit: 6 } })
      .then(({ data }) => setFeatured(data.properties))
      .catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* ── HERO ─────────────────────────────────── */}
      <section ref={heroRef} className="grain relative flex min-h-[100svh] items-end overflow-hidden" data-testid="hero-section">
        <motion.div style={{ y: imgY }} className="absolute inset-0 scale-[1.15]">
          <img src={HERO_IMG} alt="Luxury high-rise, Gurgaon" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-ink/30" />
          <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-ink/85 to-transparent" />
        </motion.div>
        <motion.div style={{ y: textY }} className="relative z-10 mx-auto w-full max-w-[1600px] px-6 pb-24 pt-40 lg:px-12">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 1 }}
            className="mb-6 font-mono text-[11px] uppercase tracking-[0.4em] text-gold"
            data-testid="hero-eyebrow"
          >
            Gurugram · Est. 2026 · Curated Marketplace
          </motion.p>
          <MaskedLines
            lines={["The City's Finest", "Homes. One", "Standard of Proof."]}
            className="max-w-5xl font-serif text-[13vw] font-light leading-[0.95] tracking-tighter text-bone sm:text-7xl lg:text-8xl"
          />
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.9 }}
            className="mt-10 flex flex-col gap-6 sm:flex-row sm:items-center"
          >
            <Link
              to="/listings"
              data-testid="hero-explore-button"
              className="group inline-flex items-center gap-3 bg-gold px-8 py-4 font-mono text-xs uppercase tracking-[0.25em] text-ink transition-colors hover:bg-bone"
            >
              Explore Residences
              <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
            </Link>
            <Link to="/register?role=builder" data-testid="hero-list-property-button" className="gold-link">
              List your property →
            </Link>
          </motion.div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6 }}
          className="absolute bottom-8 right-8 hidden flex-col items-center gap-3 lg:flex"
        >
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-smoke [writing-mode:vertical-lr]">Scroll</span>
          <span className="h-14 w-px animate-pulse-soft bg-gold" />
        </motion.div>
      </section>

      {/* ── MARQUEE ──────────────────────────────── */}
      <section className="overflow-hidden border-y border-white/10 bg-obsidian py-6" data-testid="locality-marquee" aria-hidden="true">
        <div className="animate-marquee flex w-max whitespace-nowrap">
          {[0, 1].map((copy) => (
            <div key={copy} className="flex items-center">
              {MARQUEE_ITEMS.map((item) => (
                <span key={`${copy}-${item}`} className="mx-8 flex items-center gap-8 font-serif text-2xl font-light italic text-bone/40">
                  {item}
                  <span className="h-1.5 w-1.5 rotate-45 bg-gold/60" />
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* ── STATS ────────────────────────────────── */}
      <section className="mx-auto max-w-[1600px] px-6 py-28 lg:px-12" data-testid="stats-section">
        <div className="grid grid-cols-1 gap-px bg-white/10 md:grid-cols-3">
          {[
            { icon: ShieldCheck, n: "100%", t: "Verified Listings", d: "Every residence is reviewed by our curation desk before it goes live." },
            { icon: Users, n: "5 Desks", t: "One Marketplace", d: "Developers, brokers, buyers, sales and the house — one ledger of truth." },
            { icon: TrendingUp, n: "₹500 Cr+", t: "Mandate Pipeline", d: "Active mandates across Gurgaon's most coveted addresses." },
          ].map((s, i) => (
            <div key={s.t} className="bg-ink p-10 lg:p-14">
              <Reveal delay={i * 0.12}>
                <s.icon size={20} className="text-gold" />
                <p className="mt-8 font-serif text-5xl font-light text-bone">{s.n}</p>
                <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.3em] text-gold">{s.t}</p>
                <p className="mt-4 text-sm font-light leading-relaxed text-smoke">{s.d}</p>
              </Reveal>
            </div>
          ))}
        </div>
      </section>

      {/* ── FEATURED BENTO ───────────────────────── */}
      <section className="mx-auto max-w-[1600px] px-6 pb-28 lg:px-12" data-testid="featured-section">
        <Reveal>
          <div className="mb-14 flex flex-col justify-between gap-6 md:flex-row md:items-end">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">The Collection</p>
              <h2 className="mt-3 font-serif text-4xl font-light text-bone sm:text-5xl">Featured Residences</h2>
            </div>
            <Link to="/listings" data-testid="featured-view-all-link" className="gold-link">View all →</Link>
          </div>
        </Reveal>
        <div className="grid grid-cols-1 gap-8 md:grid-cols-12">
          {featured.slice(0, 3).map((p, i) => (
            <Reveal key={p.id} delay={i * 0.1} className={i === 0 ? "md:col-span-7" : i === 1 ? "md:col-span-5" : "md:col-span-5 md:col-start-8 md:-mt-24"}>
              <PropertyCard property={p} index={i} />
            </Reveal>
          ))}
        </div>
      </section>

      {/* ── PARTNER PANEL ────────────────────────── */}
      <section className="relative overflow-hidden border-y border-white/10" data-testid="partner-section">
        <img src={MARBLE} alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="relative mx-auto grid max-w-[1600px] grid-cols-1 gap-16 px-6 py-32 lg:grid-cols-12 lg:px-12">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">For Developers & Brokers</p>
              <h2 className="mt-4 font-serif text-4xl font-light leading-tight text-bone sm:text-5xl">
                Your inventory.<br />Our machinery.
              </h2>
              <p className="mt-6 max-w-md text-sm font-light leading-relaxed text-smoke">
                Gurgaon Estates pairs every verified listing with a CRM-grade lead engine: routing rules, a dedicated sales desk,
                and a transparent commission ledger from first call to closure.
              </p>
              <Link
                to="/register?role=agent"
                data-testid="partner-cta-button"
                className="mt-10 inline-flex items-center gap-3 border border-gold px-8 py-4 font-mono text-xs uppercase tracking-[0.25em] text-gold transition-colors hover:bg-gold hover:text-ink"
              >
                Become a Partner <ArrowRight size={15} />
              </Link>
            </Reveal>
          </div>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:col-span-7">
            {[
              { n: "01", t: "Onboard", d: "Register as a developer or broker. Our desk verifies your mandate within 24 hours." },
              { n: "02", t: "List", d: "Publish residences with full specifications. Every listing passes curation review." },
              { n: "03", t: "Receive", d: "Enquiries route automatically — by locality, by asset class, by your rules." },
              { n: "04", t: "Close", d: "Track site visits, negotiations and commissions in one transparent ledger." },
            ].map((s, i) => (
              <Reveal key={s.n} delay={i * 0.1} className={i % 2 === 1 ? "sm:translate-y-10" : ""}>
                <div className="border border-white/15 bg-black/60 p-8 backdrop-blur-xl transition-colors duration-500 hover:border-gold/50">
                  <p className="font-mono text-xs text-gold">{s.n}</p>
                  <p className="mt-4 font-serif text-2xl text-bone">{s.t}</p>
                  <p className="mt-3 text-sm font-light leading-relaxed text-smoke">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── CLOSING BAND ─────────────────────────── */}
      <section className="grain relative overflow-hidden" data-testid="closing-section">
        <img src={VILLA} alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/70 to-transparent" />
        <div className="relative mx-auto max-w-[1600px] px-6 py-36 lg:px-12">
          <Reveal>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">Begin</p>
            <h2 className="mt-4 max-w-3xl font-serif text-5xl font-light leading-tight text-bone sm:text-6xl">
              Find the residence<br />that finds you back.
            </h2>
            <div className="mt-12 flex flex-col gap-6 sm:flex-row sm:items-center">
              <Link
                to="/register"
                data-testid="closing-cta-button"
                className="inline-flex items-center gap-3 bg-gold px-8 py-4 font-mono text-xs uppercase tracking-[0.25em] text-ink transition-colors hover:bg-bone"
              >
                Create an Account <ArrowRight size={15} />
              </Link>
              <Link to="/listings" className="gold-link" data-testid="closing-browse-link">Browse first →</Link>
            </div>
          </Reveal>
        </div>
        <img src={INTERIOR} alt="" className="pointer-events-none absolute -right-10 bottom-0 hidden w-[38%] opacity-60 mix-blend-luminosity xl:block" />
      </section>
    </div>
  );
}
